import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CustomField, Lead } from '../api/types';
import { CustomValue, StatusChip, formatDateTime, relativeTime } from '../components/ui';
import { CopyPhone } from '../components/CopyPhone';
import { CallButton } from '../components/CallButton';

/**
 * Lead table columns.
 *
 * Core columns are fixed here; custom-field columns are derived from the
 * registry, so a field an admin creates becomes available as a column with no
 * code change — the same principle as forms and filters.
 */

export interface ColumnDef {
  id: string;
  label: string;
  /** 'core' or 'custom' — used to group the picker. */
  group: 'core' | 'custom';
  /** The name column is the link to the lead, so it is always shown. */
  locked?: boolean;
  align?: 'left' | 'right';
  render: (lead: Lead, ctx: RenderContext) => ReactNode;
}

interface RenderContext {
  byKey: Map<string, CustomField>;
}

const CORE_COLUMNS: ColumnDef[] = [
  {
    id: 'name',
    label: 'Name',
    group: 'core',
    locked: true,
    render: (lead) => (
      <>
        <strong>{lead.full_name}</strong>
        {lead.is_duplicate && (
          <span className="chip chip-dup" style={{ marginLeft: 6 }}>
            duplicate
          </span>
        )}
      </>
    ),
  },
  {
    id: 'phone',
    label: 'Phone',
    group: 'core',
    render: (lead) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <CopyPhone phone={lead.phone} />
        {/* Renders nothing when the user has no calling device, no permission,
            or the deployment has no PBX — so the column is unchanged for them. */}
        <CallButton leadId={lead.id} phone={lead.phone} compact />
      </span>
    ),
  },
  {
    id: 'email',
    label: 'Email',
    group: 'core',
    render: (lead) => <span className="mono truncate">{lead.email ?? '—'}</span>,
  },
  {
    id: 'status',
    label: 'Status',
    group: 'core',
    render: (lead) => <StatusChip status={lead.status} />,
  },
  {
    id: 'assigned_to',
    label: 'Assigned to',
    group: 'core',
    render: (lead) =>
      lead.assigned_to ? (
        <span title={lead.assigned_at ? `Assigned: ${formatDateTime(lead.assigned_at)}` : undefined}>
          {lead.assigned_to.full_name}
        </span>
      ) : (
        <span style={{ color: 'var(--faint)' }}>Unassigned</span>
      ),
  },
  {
    id: 'assigned_at',
    label: 'Assigned date',
    group: 'core',
    align: 'right',
    render: (lead) =>
      lead.assigned_at ? (
        <span title={new Date(lead.assigned_at).toLocaleString()}>
          {formatDateTime(lead.assigned_at)}
        </span>
      ) : (
        <span style={{ color: 'var(--faint)' }}>—</span>
      ),
  },
  {
    id: 'team',
    label: 'Team',
    group: 'core',
    render: (lead) => (lead.team ? <span className="tag">{lead.team.name}</span> : '—'),
  },
  {
    id: 'source',
    label: 'Source',
    group: 'core',
    render: (lead) => <span className="chip chip-plain">{lead.source.name}</span>,
  },
  {
    id: 'campaign',
    label: 'Campaign',
    group: 'core',
    render: (lead) => (lead.campaign ? <span className="truncate">{lead.campaign.name}</span> : '—'),
  },
  {
    id: 'registration_date',
    label: 'Registration date',
    group: 'core',
    align: 'right',
    // The calendar date a lead registered. Same instant as Created, which
    // shows it as "3d ago" — useful for sorting by eye, useless for telling
    // someone which day the lead actually came in.
    render: (lead) => (
      <span title={new Date(lead.created_at).toLocaleString()}>
        {new Date(lead.created_at).toLocaleDateString(undefined, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}
      </span>
    ),
  },
  {
    id: 'created_at',
    label: 'Created',
    group: 'core',
    align: 'right',
    render: (lead) => (
      <span title={new Date(lead.created_at).toLocaleString()}>{relativeTime(lead.created_at)}</span>
    ),
  },
  {
    id: 'last_activity_at',
    label: 'Last activity',
    group: 'core',
    align: 'right',
    render: (lead) => relativeTime(lead.last_activity_at),
  },
  {
    id: 'next_follow_up_at',
    label: 'Next follow-up',
    group: 'core',
    align: 'right',
    render: (lead) => {
      if (!lead.next_follow_up_at) return '—';
      const due = new Date(lead.next_follow_up_at);
      const overdue = due.getTime() < Date.now();
      return (
        <span style={overdue ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
          {due.toLocaleDateString()}
          {overdue ? ' (overdue)' : ''}
        </span>
      );
    },
  },
];

/** Shown when a user has never touched the picker. */
const DEFAULT_VISIBLE = [
  'name', 'phone', 'status', 'assigned_to', 'assigned_at', 'source', 'campaign', 'registration_date', 'created_at',
];
const DEFAULT_CUSTOM_COUNT = 2;

/**
 * Custom fields kept out of the leads screen — no column, no filter. The field
 * itself is untouched: still captured on the form, still on the lead, still
 * exportable, and still filterable through the API. This is a display choice,
 * which is why it lives here and not in the field registry.
 */
export const HIDDEN_CUSTOM_FIELDS = new Set(['cf_school_name']);

/**
 * Bumped when a column change has to reach people who already have a saved
 * layout — otherwise their stored list wins and they never see it. The saved
 * list is migrated, not thrown away, so every other choice survives.
 */
const PREFS_REVISION = 3;
const DROPPED_ON_UPGRADE = new Set([...HIDDEN_CUSTOM_FIELDS].map((key) => `cf:${key}`));
const ADDED_ON_UPGRADE = ['registration_date', 'assigned_at'];

function defaultVisibleIds(columns: ColumnDef[]): string[] {
  const firstCustom = columns
    .filter((c) => c.group === 'custom' && !HIDDEN_CUSTOM_FIELDS.has(c.id.replace(/^cf:/, '')))
    .slice(0, DEFAULT_CUSTOM_COUNT)
    .map((c) => c.id);
  return [...DEFAULT_VISIBLE, ...firstCustom];
}

export function buildColumns(fields: CustomField[]): ColumnDef[] {
  const customColumns: ColumnDef[] = fields
    .filter((f) => f.is_active)
    .map((field) => ({
      id: `cf:${field.api_key}`,
      label: field.label,
      group: 'custom' as const,
      render: (lead: Lead, ctx: RenderContext) => (
        <span className="truncate" style={{ display: 'inline-block', maxWidth: 220 }}>
          <CustomValue apiKey={field.api_key} value={lead.custom_fields[field.api_key]} byKey={ctx.byKey} />
        </span>
      ),
    }));

  // Custom fields sit between the pipeline columns and the timestamps, which is
  // where they read most naturally on a lead row.
  const tailIds = new Set([
    'assigned_at', 'source', 'campaign', 'registration_date', 'created_at', 'last_activity_at', 'next_follow_up_at',
  ]);
  const head = CORE_COLUMNS.filter((c) => !tailIds.has(c.id));
  const tail = CORE_COLUMNS.filter((c) => tailIds.has(c.id));
  return [...head, ...customColumns, ...tail];
}

/* ------------------------------ persistence ------------------------------ */

const storageKey = (userId: string | undefined) => `crm.lead_columns.${userId ?? 'anon'}`;
const revisionKey = (userId: string | undefined) => `crm.lead_columns_rev.${userId ?? 'anon'}`;

/**
 * Remembers the chosen columns per user, in localStorage.
 *
 * Deliberately not stored server-side: it is a personal display preference, it
 * must survive a reload instantly, and syncing it would mean a table, an
 * endpoint and a round trip on every page load for no real gain.
 */
export function useColumnPreferences(fields: CustomField[], userId: string | undefined) {
  const columns = useMemo(() => buildColumns(fields), [fields]);

  const [visibleIds, setVisibleIds] = useState<string[] | null>(null);

  // Load once the registry is known, so defaults can include custom fields.
  useEffect(() => {
    if (columns.length === 0) return;
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as string[];
        // Drop ids for fields that have since been deleted or deactivated.
        const known = new Set(columns.map((c) => c.id));
        let kept = parsed.filter((id) => known.has(id));

        // Apply column changes made since this layout was saved, once.
        if (Number(localStorage.getItem(revisionKey(userId)) ?? 0) < PREFS_REVISION) {
          kept = kept.filter((id) => !DROPPED_ON_UPGRADE.has(id));
          for (const id of ADDED_ON_UPGRADE) {
            if (known.has(id) && !kept.includes(id)) kept.push(id);
          }
          try {
            localStorage.setItem(storageKey(userId), JSON.stringify(kept));
            localStorage.setItem(revisionKey(userId), String(PREFS_REVISION));
          } catch {
            // Private browsing — the migration just runs again next load.
          }
        }

        if (kept.length > 0) {
          setVisibleIds(kept);
          return;
        }
      } catch {
        // Corrupt preference — fall through to defaults rather than breaking.
      }
    }
    setVisibleIds(defaultVisibleIds(columns));
  }, [columns, userId]);

  const persist = useCallback(
    (ids: string[]) => {
      setVisibleIds(ids);
      try {
        localStorage.setItem(storageKey(userId), JSON.stringify(ids));
      } catch {
        // Private browsing or a full quota — the choice just won't persist.
      }
    },
    [userId],
  );

  const toggle = useCallback(
    (id: string) => {
      if (!visibleIds) return;
      const column = columns.find((c) => c.id === id);
      if (column?.locked) return;
      persist(
        visibleIds.includes(id) ? visibleIds.filter((x) => x !== id) : [...visibleIds, id],
      );
    },
    [visibleIds, columns, persist],
  );

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(storageKey(userId));
      localStorage.setItem(revisionKey(userId), String(PREFS_REVISION));
    } catch {
      /* ignore */
    }
    setVisibleIds(defaultVisibleIds(columns));
  }, [columns, userId]);

  // Rendered in the canonical column order, not the order they were ticked.
  const visibleColumns = useMemo(() => {
    if (!visibleIds) return [];
    const set = new Set(visibleIds);
    return columns.filter((c) => c.locked || set.has(c.id));
  }, [columns, visibleIds]);

  return {
    columns,
    visibleColumns,
    visibleIds: visibleIds ?? [],
    toggle,
    reset,
    ready: visibleIds !== null,
  };
}
